import React, { useState, useEffect } from 'react';
import { Box, Typography, useMediaQuery } from '@material-ui/core';
import { motion, AnimatePresence } from 'framer-motion';

const DynamicHUD = ({ visible, onComplete, persistent = false }) => {
  const [heartRate, setHeartRate] = useState(72);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [connectionSpeed, setConnectionSpeed] = useState(0);
  // Detect mobile screens
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Animate heart rate
  useEffect(() => {
    if (!visible) return;
    
    const interval = setInterval(() => {
      setHeartRate(prev => {
        const change = Math.random() > 0.5 ? 1 : -1;
        const newRate = prev + change;
        return Math.max(65, Math.min(85, newRate)); // Keep between 65-85
      });
    }, 800);

    return () => clearInterval(interval);
  }, [visible]);

  // Update clock every second
  useEffect(() => {
    if (!visible) return;
    
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, [visible]);

  // Measure connection speed
  useEffect(() => {
    if (!visible) return;

    const measureSpeed = async () => {
      try {
        const startTime = performance.now();
        const response = await fetch('https://www.google.com/favicon.ico?cache=' + Date.now(), { mode: 'no-cors' });
        const endTime = performance.now();
        const duration = (endTime - startTime) / 1000; // seconds
        const fileSizeInBytes = 1024; // approximate favicon size
        const speedKbps = Math.round((fileSizeInBytes * 8) / duration / 1000);
        setConnectionSpeed(speedKbps);
      } catch (error) {
        setConnectionSpeed(Math.floor(Math.random() * 500) + 500); // fallback random speed
      }
    };

    measureSpeed();
    const interval = setInterval(measureSpeed, 5000); // Update every 5 seconds

    return () => clearInterval(interval);
  }, [visible]);

  // Auto-hide after 3 seconds
  useEffect(() => {
    if (visible && onComplete && !persistent) {
      const timer = setTimeout(() => {
        onComplete();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [visible, onComplete, persistent]);

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: 'none',
            zIndex: 10003
          }}
        >
          {/* Top Bar */}
          <Box style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: isMobile ? '50px' : '60px',
            background: 'linear-gradient(0deg, rgba(0, 255, 255, 0.45) 0%, rgba(0, 255, 255, 0.15) 60%, transparent 100%)',
            borderBottom: '2px solid rgba(0, 255, 255, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: isMobile ? '0 0.5rem' : '0 2rem'
          }}>
            {/* Left: Heart Rate */}
            <Box style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '0.3rem' : '1rem' }}>
              <motion.div
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 0.8, repeat: Infinity }}
                style={{
                  fontSize: isMobile ? '1rem' : '1.5rem',
                  filter: 'drop-shadow(0 0 8px rgba(0, 255, 255, 0.8))'
                }}
              >
                ♥
              </motion.div>
              <Typography style={{
                fontFamily: '"Share Tech Mono", monospace',
                fontSize: isMobile ? '0.7rem' : '1.2rem',
                color: '#00ffff',
                fontWeight: 'bold',
                textShadow: '0 0 10px rgba(0, 255, 255, 0.8)'
              }}>
                {heartRate}
              </Typography>
              {/* Mini ECG line - hide on mobile */}
              {!isMobile && (
                <svg width="80" height="30" style={{ marginLeft: '0.5rem' }}>
                  <motion.path
                    d="M0,15 L20,15 L25,5 L30,25 L35,15 L80,15"
                    stroke="#00ffff"
                    strokeWidth="2"
                    fill="none"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 0.8, repeat: Infinity }}
                    style={{ filter: 'drop-shadow(0 0 4px rgba(0, 255, 255, 0.8))' }}
                  />
                </svg>
              )}
            </Box>

            {/* Center: Time & Date */}
            <Box style={{ textAlign: 'center' }}>
              <Typography style={{
                fontFamily: '"Orbitron", monospace',
                fontSize: isMobile ? '0.9rem' : '1.5rem',
                color: '#00ffff',
                fontWeight: 'bold',
                textShadow: '0 0 10px rgba(0, 255, 255, 0.8)',
                letterSpacing: isMobile ? '0.1em' : '0.2em'
              }}>
                {formatTime(currentTime)}
              </Typography>
              {!isMobile && (
                <Typography style={{
                  fontFamily: '"Share Tech Mono", monospace',
                  fontSize: '0.9rem',
                  color: '#00ffff',
                  opacity: 0.7
                }}>
                  {formatDate(currentTime)}
                </Typography>
              )}
            </Box>

            {/* Right: Connection & Battery */}
            <Box style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '0.5rem' : '2rem' }}>
              {/* WiFi Signal */}
              <Box style={{ textAlign: 'center' }}>
                <Box style={{ display: 'flex', alignItems: 'flex-end', gap: '2px', marginBottom: isMobile ? '0' : '0.25rem', justifyContent: 'center' }}>
                  {[1, 2, 3, 4].map((bar) => (
                    <motion.div
                      key={bar}
                      animate={{ opacity: connectionSpeed > bar * 37.5 ? 1 : 0.3 }}
                      transition={{ duration: 0.3 }}
                      style={{
                        width: isMobile ? '4px' : '6px',
                        height: isMobile ? `${bar * 4}px` : `${bar * 6}px`,
                        background: connectionSpeed > bar * 37.5 ? '#00ffff' : '#00ffff40',
                        borderRadius: '2px',
                        boxShadow: connectionSpeed > bar * 37.5 ? '0 0 8px rgba(0, 255, 255, 0.6)' : 'none'
                      }}
                    />
                  ))}
                </Box>
                {!isMobile && (
                  <Typography style={{
                    fontFamily: '"Share Tech Mono", monospace',
                    fontSize: '0.75rem',
                    color: '#00ffff',
                    fontWeight: 'bold',
                    textShadow: '0 0 8px rgba(0, 255, 255, 0.6)'
                  }}>
                    {connectionSpeed} Kbps
                  </Typography>
                )}
              </Box>

              {/* Battery */}
              <Box style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '0.25rem' : '0.5rem' }}>
                <Typography style={{
                  fontFamily: '"Share Tech Mono", monospace',
                  fontSize: isMobile ? '0.7rem' : '0.9rem',
                  color: '#00ffff',
                  fontWeight: 'bold'
                }}>
                  98%
                </Typography>
                <Box style={{
                  width: isMobile ? '30px' : '50px',
                  height: isMobile ? '16px' : '24px',
                  border: '2px solid #00ffff',
                  borderRadius: '3px',
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '2px'
                }}>
                  <Box style={{
                    width: '98%',
                    height: '100%',
                    background: 'linear-gradient(90deg, #00ffff, #00ff88)',
                    borderRadius: '2px',
                    boxShadow: '0 0 8px rgba(0, 255, 255, 0.6)'
                  }} />
                  <Box style={{
                    position: 'absolute',
                    right: '-5px',
                    width: '3px',
                    height: isMobile ? '8px' : '10px',
                    background: '#00ffff',
                    borderRadius: '0 2px 2px 0'
                  }} />
                </Box>
              </Box>
            </Box>
          </Box>


          {/* Bottom Bar */}
          <Box style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: isMobile ? '60px' : '80px',
            background: 'linear-gradient(180deg, rgba(0, 255, 255, 0.45) 0%, rgba(0, 255, 255, 0.15) 60%, transparent 100%)',
            borderTop: '2px solid rgba(0, 255, 255, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: isMobile ? '0 0.5rem' : '0 2rem',
            overflow: 'hidden'
          }}>
            {/* Center: Objective */}
            <Box style={{ textAlign: 'center', position: 'relative' }}>
              {/* Scan line only over objective */}
              <motion.div
                animate={{ x: ['-100%', '200%'] }}
                transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100px',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(0, 255, 255, 0.3), transparent)',
                  pointerEvents: 'none',
                  zIndex: 1
                }}
              />
              {!isMobile && (
                <Typography style={{
                  fontFamily: '"Share Tech Mono", monospace',
                  fontSize: '0.8rem',
                  color: '#00ff88',
                  textShadow: '0 0 8px rgba(0, 255, 136, 0.6)',
                  letterSpacing: '0.1em'
                }}>
                  OBJECTIVE
                </Typography>
              )}
              <Typography style={{
                fontFamily: '"Orbitron", monospace',
                fontSize: isMobile ? '0.7rem' : '1.2rem',
                color: '#00ffff',
                fontWeight: 'bold',
                textShadow: '0 0 10px rgba(0, 255, 255, 0.8)',
                letterSpacing: isMobile ? '0.02em' : '0.05em'
              }}>
                EXPLORE CANDIDATE
              </Typography>
            </Box>

          </Box>

          {/* Scan lines */}
          <motion.div
            animate={{ y: ['0%', '100%'] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '2px',
              background: 'linear-gradient(90deg, transparent, #00ffff, transparent)',
              opacity: 0.5
            }}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default DynamicHUD;
